import {Component, OnDestroy, OnInit} from '@angular/core';
import {MenuItem} from 'primeng/api';
import {ActivatedRoute, NavigationEnd, PRIMARY_OUTLET, Router} from '@angular/router';
import {filter, takeUntil} from 'rxjs/operators';
import {Location} from '@angular/common';
import {Subject} from 'rxjs';
import {LmsHeaderCommonService} from '../lms-header/service/lms-header-common.service';

/**
 * Possible display modes for the breadcrumb component.
 *  - `Back`       – shows a back chevron with the current route label.
 *  - `BackButton` – shows a back button with the current route label.
 *  - `Breadcrumb` – renders the full breadcrumb trail.
 *  - `Hidden`     – nothing is rendered.
 *
 * Driven by route `data` conventions:
 *  - `data: { breadcrumb: 'Label' }` — `Breadcrumb` mode, label shown in the trail.
 *  - `data: { breadcrumb: null }`    — `Breadcrumb` mode without contributing a label
 *                                      (used on dynamic `:id` segments).
 *  - `data: { showBack: true, breadcrumb: 'Label' }` — `Back` mode.
 *  - `data: { showButton: true, breadcrumb: 'Label' }` — `BackButton` mode.
 *  - No `data` / no `breadcrumb` key — `Hidden` mode.
 */
export enum DisplayMode {
  Back = 'back',
  BackButton = 'back-button',
  Breadcrumb = 'breadcrumb',
  Hidden = 'hidden',
}

@Component({
  selector: 'app-lms-breadcrumb',
  templateUrl: './lms-breadcrumb.component.html',
  styleUrls: ['./lms-breadcrumb.component.scss'],
})
export class LmsBreadcrumbComponent implements OnInit, OnDestroy {
  private static readonly BREADCRUMB_KEY = 'breadcrumb';
  private static readonly SHOW_BACK_KEY = 'showBack';
  private static readonly SHOW_BUTTON_KEY = 'showButton';

  menuItems: MenuItem[] = [];
  showBreadcrumb = false;
  showBack = false;
  currentRouteLabel = '';

  private currentUrlPath = '';
  private readonly destroy$ = new Subject<void>();

  constructor(
    private readonly router: Router,
    private readonly activatedRoute: ActivatedRoute,
    private readonly location: Location,
    private readonly lmsHeaderCommonService: LmsHeaderCommonService,
  ) {}

  ngOnInit(): void {
    this.router.events
      .pipe(
        filter(event => event instanceof NavigationEnd),
        takeUntil(this.destroy$),
      )
      .subscribe(() => this.refresh());
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private refresh(): void {
    this.currentUrlPath = this.router.url.split('?')[0];
    const mode = this.getDisplayMode();

    this.showBreadcrumb = mode === DisplayMode.Breadcrumb;
    this.showBack = mode === DisplayMode.Back;
    this.lmsHeaderCommonService.setShowButton(mode === DisplayMode.BackButton);

    if (mode === DisplayMode.Back || mode === DisplayMode.BackButton) {
      const lastNamed = this.getLastNamedRoute(this.activatedRoute.root);
      this.currentRouteLabel = lastNamed?.snapshot.data[LmsBreadcrumbComponent.BREADCRUMB_KEY] ?? 'Back';
      this.lmsHeaderCommonService.setbreadCrumLable(this.currentRouteLabel);
    } else if (mode === DisplayMode.Breadcrumb) {
      this.menuItems = this.buildBreadcrumbs(this.activatedRoute.root);
    }
  }

  private getDisplayMode(): DisplayMode {
    const lastNamed = this.getLastNamedRoute(this.activatedRoute.root);
    if (!lastNamed) { return DisplayMode.Hidden; }

    const data = lastNamed.snapshot.data;

    if (data[LmsBreadcrumbComponent.SHOW_BACK_KEY]) { return DisplayMode.Back; }
    if (data[LmsBreadcrumbComponent.SHOW_BUTTON_KEY]) { return DisplayMode.BackButton; }
    if (LmsBreadcrumbComponent.BREADCRUMB_KEY in data) { return DisplayMode.Breadcrumb; }
    if (Object.keys(data).length > 0) { return DisplayMode.Hidden; }

    return this.hasAncestorWithBreadcrumb(this.activatedRoute.root) ? DisplayMode.Breadcrumb : DisplayMode.Hidden;
  }

  private getLastNamedRoute(root: ActivatedRoute): ActivatedRoute | null {
    let lastNamed: ActivatedRoute | null = null;
    let current: ActivatedRoute = root;
    while (true) {
      if (current.snapshot.url.length > 0) {
        lastNamed = current;
      }
      const primaryChild = current.children.find(c => c.outlet === PRIMARY_OUTLET);
      if (!primaryChild) { break; }
      current = primaryChild;
    }
    return lastNamed;
  }

  private hasAncestorWithBreadcrumb(root: ActivatedRoute): boolean {
    let current: ActivatedRoute = root;
    while (true) {
      if (LmsBreadcrumbComponent.BREADCRUMB_KEY in current.snapshot.data) { return true; }
      const primaryChild = current.children.find(c => c.outlet === PRIMARY_OUTLET);
      if (!primaryChild) { break; }
      current = primaryChild;
    }
    return false;
  }

  private buildBreadcrumbs(
    route: ActivatedRoute,
    builtUrl = '',
    crumbs: MenuItem[] = [],
  ): MenuItem[] {
    for (const child of route.children) {
      if (child.outlet !== PRIMARY_OUTLET) { continue; }

      const segment = child.snapshot.url.map(s => s.path).join('/');
      const fullUrl = segment ? `${builtUrl}/${segment}` : builtUrl;

      if (segment && LmsBreadcrumbComponent.BREADCRUMB_KEY in child.snapshot.data) {
        const label: string | null = child.snapshot.data[LmsBreadcrumbComponent.BREADCRUMB_KEY];
        if (label !== null && label !== undefined) {
          const isCurrentPage = fullUrl === this.currentUrlPath;
          crumbs.push(
            isCurrentPage
              ? { label }
              : { label, url: `#${fullUrl}`, target: '_self' },
          );
        }
      }

      this.buildBreadcrumbs(child, fullUrl, crumbs);
    }
    return crumbs;
  }

  backClicked(): void {
    this.location.back();
  }
}
